import { usePostMatchACHIncomingMutation } from 'API/accounting/AccountingApi';
import { IPotentialMatch, useGetPotentialEscrowFile, useGetPotentialOrderInfo } from './IPotentialMatch';
import React, { useEffect, useState } from 'react';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { ErrorMessageTW } from 'tailwindComponents/errorMessage/ErrorMessageTW';
import { convertToError } from 'API/BaseApi';
import {
  useCreateACHDepositMutation,
  useDeleteCheckDepositMutation,
  useDeleteWireDepositMutation,
} from 'API/order/escrow/EscrowBaseAPI';
import { IACHForm } from './Forms';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';
import { ConfirmACHForm } from './ConfirmACHForm';
import { PotentialDisplay } from './PotentialDisplay';
import { assertDefined } from 'utilities/Assertions';

export interface IConfirmACHMatchProps {
  actualId: string;
  accountNumber: string;
  potentialMatch: IPotentialMatch;
  onClose: () => void;
  amountCents: number;
  amountCentsMax: number;
}

export const ConfirmACHMatch: React.FC<IConfirmACHMatchProps> = ({
  actualId,
  accountNumber,
  potentialMatch,
  onClose,
  amountCents,
  amountCentsMax,
}) => {
  const order = useGetPotentialOrderInfo(potentialMatch);
  const escrowFile = useGetPotentialEscrowFile(potentialMatch);
  const [doMatch, { error }] = usePostMatchACHIncomingMutation();
  const [createIncomingACH, { isLoading: isLoadingCreateIncomingACH }] = useCreateACHDepositMutation();
  const [deleteIncomingWire] = useDeleteWireDepositMutation();
  const [deleteIncomingCheck] = useDeleteCheckDepositMutation();
  const [expectedACH, setExpectedACH] = useState<IEscrowExpectedIncomingAchData | undefined>(
    potentialMatch.expectedACH
  );
  useEffect(() => {
    setExpectedACH(potentialMatch.expectedACH);
  }, [potentialMatch, setExpectedACH]);

  const onMatch = async () => {
    assertDefined(expectedACH);
    const result = await doMatch({ actualId, expectedId: expectedACH.id });

    if (checkForMutationSuccess(result, 'matching ach')) {
      if (potentialMatch.expectedWire?.id) {
        await deleteIncomingWire({ id: potentialMatch.expectedWire.id, orderId: order.id });
      }
      if (potentialMatch.expectedCheck?.id) {
        await deleteIncomingCheck({ id: potentialMatch.expectedCheck.id, orderId: order.id });
      }
      onClose();
    }
  };

  const onCreate = async (x: IACHForm) => {
    if (order?.id && escrowFile?.id && x.amountCents && x.payerType) {
      const result = await createIncomingACH({
        ...x,
        amountCents: x.amountCents,
        payerType: x.payerType,
        orderId: order.id,
        escrowFileId: escrowFile.id,
      });

      checkForMutationSuccess(result, 'creating an expected wire');
      if ('data' in result) {
        setExpectedACH({
          ...result.data,
          escrowFile: { ...escrowFile, order: order },
        } as any);
      }
    }
  };

  return (
    <>
      <ErrorMessageTW error={convertToError(error)} />
      {!expectedACH && !isLoadingCreateIncomingACH && (
        <ConfirmACHForm
          potentialMatch={potentialMatch}
          createExpected={onCreate}
          amountCents={amountCents}
          amountCentsMax={amountCentsMax}
        />
      )}
      {isLoadingCreateIncomingACH && <SpinnerTW />}
      <PotentialDisplay
        potentialMatch={expectedACH ? { expectedACH } : potentialMatch}
        accountNumber={accountNumber}
        onMatch={expectedACH && onMatch}
      />
    </>
  );
};
